import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CareerChallengePrizeTierDto,
  CareerChallengeSponsorDto,
  CareerChallengeSubmissionDto,
  CareerChallengeWinnerDto,
  UpdateCareerChallengeRequest
} from '../../../../models';
import { CareerChallengeAdminStore } from '../shared/career-challenge-admin.store';
import { ImageUrlFieldComponent } from '../../../../shared/challenge-media/image-url-field.component';
import { resolveChallengeMediaSrc } from '../../../../shared/challenge-media/challenge-media.util';

@Component({
  selector: 'app-career-challenge-prizes-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatSnackBarModule,
    ImageUrlFieldComponent
  ],
  templateUrl: './prizes-tab.component.html',
  styleUrl: './tab-shared.scss'
})
export class CareerChallengePrizesTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly store = inject(CareerChallengeAdminStore);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  busy = false;
  editingId: string | null = null;
  editingDisplayUrl: string | null = null;
  rankedSubmissions: CareerChallengeSubmissionDto[] = [];
  participationEnabled = true;

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    description: [''],
    prizeValue: [''],
    sponsorId: [''],
    placementFrom: [1, Validators.required],
    placementCount: [1, Validators.required],
    photoUrl: [''],
    sortOrder: [0, Validators.required]
  });

  readonly overrideForm = this.fb.nonNullable.group({
    prizeTierId: ['', Validators.required],
    rank: [1, Validators.required],
    candidateKey: ['', Validators.required]
  });

  get id(): string {
    return this.route.parent?.snapshot.paramMap.get('id') ?? '';
  }

  get prizes(): CareerChallengePrizeTierDto[] {
    return this.store.challenge()?.prizeTiers ?? [];
  }

  get sponsors(): CareerChallengeSponsorDto[] {
    return this.store.challenge()?.sponsors ?? [];
  }

  get isOpenAccess(): boolean {
    const mode = this.store.challenge()?.access?.mode;
    return !mode || mode === 'open';
  }

  get isFirstSgaEvaluation(): boolean {
    return this.store.challenge()?.access?.mode === 'first_sga_evaluation';
  }

  get showParticipationPrize(): boolean {
    return this.isOpenAccess && !this.isFirstSgaEvaluation;
  }

  /** First-SGA evaluation always includes free remediation; no admin toggle. */
  get showFirstSgaFreeRemediationNote(): boolean {
    return this.isFirstSgaEvaluation;
  }

  get winners(): CareerChallengeWinnerDto[] {
    return this.store.challenge()?.winners ?? [];
  }

  get canOverrideWinners(): boolean {
    const challenge = this.store.challenge();
    return !!challenge?.judgingFinalizedAt || challenge?.status === 'completed';
  }

  get loading() {
    return this.store.loading();
  }

  ngOnInit(): void {
    void this.bootstrap();
  }

  async bootstrap(): Promise<void> {
    await this.store.load(this.id);
    this.syncParticipationFromStore();
    await this.loadRankedSubmissions();
  }

  private syncParticipationFromStore(): void {
    const prize = this.store.challenge()?.participationPrize;
    this.participationEnabled = prize?.enabled !== false;
  }

  async saveParticipationPrize(): Promise<void> {
    const challenge = this.store.challenge();
    if (!challenge || !this.isOpenAccess) {
      return;
    }
    this.busy = true;
    try {
      const payload: UpdateCareerChallengeRequest = {
        title: challenge.title,
        slug: challenge.slug,
        type: challenge.type,
        summary: challenge.summary ?? null,
        description: challenge.description ?? null,
        showOnWebsite: challenge.showOnWebsite,
        coverImageUrl: challenge.coverImageUrl ?? null,
        ipPolicy: challenge.ipPolicy,
        faqItems: challenge.faqItems ?? [],
        audience: challenge.audience,
        access: challenge.access ?? { mode: 'open', prerequisiteSkillIds: [] },
        schedule: challenge.schedule ?? null,
        participationPolicy: challenge.participationPolicy ?? null,
        participationPrize: {
          enabled: this.participationEnabled,
          type: 'remediation_learning_plan',
          audience: 'scored_participants_with_gaps'
        },
        skillMappings: challenge.skillMappings ?? [],
        rubricCriteria: challenge.rubricCriteria ?? [],
        rubricWeights: challenge.rubricWeights ?? []
      };
      await firstValueFrom(this.api.updateCareerChallenge(this.id, payload));
      this.snackBar.open('Participation prize saved', 'Close', { duration: 2200 });
      await this.store.load(this.id, true);
      this.syncParticipationFromStore();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  sponsorName(sponsorId?: string | null): string {
    if (!sponsorId) {
      return '';
    }
    return this.sponsors.find((s) => s.id === sponsorId)?.name ?? sponsorId;
  }

  async loadRankedSubmissions(): Promise<void> {
    try {
      const submissions = await firstValueFrom(this.api.listAdminCareerChallengeSubmissions(this.id));
      this.rankedSubmissions = [...(submissions ?? [])]
        .filter((s) => s.score != null)
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    } catch {
      this.rankedSubmissions = [];
    }
  }

  edit(prize: CareerChallengePrizeTierDto): void {
    this.editingId = prize.id;
    this.form.patchValue({
      name: prize.name,
      description: prize.description ?? '',
      prizeValue: prize.prizeValue ?? '',
      sponsorId: prize.sponsorId ?? '',
      placementFrom: prize.placementFrom || 1,
      placementCount: prize.placementCount || 1,
      photoUrl: prize.photoUrl ?? '',
      sortOrder: prize.sortOrder
    });
    this.editingDisplayUrl = prize.photoDisplayUrl ?? null;
  }

  reset(): void {
    this.editingId = null;
    this.editingDisplayUrl = null;
    this.form.reset({
      name: '',
      description: '',
      prizeValue: '',
      sponsorId: '',
      placementFrom: 1,
      placementCount: 1,
      photoUrl: '',
      sortOrder: 0
    });
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.busy = true;
    try {
      const raw = this.form.getRawValue();
      await firstValueFrom(
        this.api.upsertCareerChallengePrize(
          this.id,
          {
            name: raw.name.trim(),
            description: raw.description.trim(),
            prizeValue: raw.prizeValue.trim(),
            sponsorId: raw.sponsorId.trim() || null,
            placementFrom: Number(raw.placementFrom) || 1,
            placementCount: Number(raw.placementCount) || 1,
            photoUrl: raw.photoUrl.trim() || null,
            sortOrder: Number(raw.sortOrder) || 0
          },
          this.editingId ?? undefined
        )
      );
      this.snackBar.open('Prize saved', 'Close', { duration: 2200 });
      this.reset();
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  async remove(prizeId: string): Promise<void> {
    if (!confirm('Delete this prize tier?')) {
      return;
    }
    this.busy = true;
    try {
      await firstValueFrom(this.api.deleteCareerChallengePrize(this.id, prizeId));
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  beginOverride(winner: CareerChallengeWinnerDto): void {
    const candidateKey = winner.teamId
      ? `team:${winner.teamId}`
      : winner.userId
        ? `user:${winner.userId}`
        : '';
    this.overrideForm.patchValue({
      prizeTierId: winner.prizeTierId,
      rank: winner.rank,
      candidateKey
    });
  }

  beginOverrideForPrize(prize: CareerChallengePrizeTierDto): void {
    this.overrideForm.patchValue({
      prizeTierId: prize.id,
      rank: prize.placementFrom || 1,
      candidateKey: ''
    });
  }

  candidateLabel(submission: CareerChallengeSubmissionDto, index: number): string {
    const teamName = submission.teamId
      ? this.store.challenge()?.teams?.find((t) => t.id === submission.teamId)?.name
      : null;
    const name =
      submission.teamName ||
      teamName ||
      submission.teamId ||
      submission.userId ||
      submission.id;
    const score = submission.score != null ? ` · score ${submission.score}` : '';
    return `#${index + 1} ${name}${score}`;
  }

  async applyOverride(): Promise<void> {
    if (this.overrideForm.invalid || !this.canOverrideWinners) {
      return;
    }
    const raw = this.overrideForm.getRawValue();
    const candidate = this.parseCandidateKey(raw.candidateKey);
    if (!candidate) {
      this.snackBar.open('Pick a ranked submission', 'Close', { duration: 3000 });
      return;
    }
    this.busy = true;
    try {
      await firstValueFrom(
        this.api.overrideCareerChallengeWinner(this.id, raw.prizeTierId, {
          rank: Number(raw.rank) || 1,
          teamId: candidate.teamId,
          userId: candidate.userId,
          clear: false
        })
      );
      this.snackBar.open('Winner updated', 'Close', { duration: 2200 });
      await this.store.load(this.id, true);
      await this.loadRankedSubmissions();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  async clearWinner(winner: CareerChallengeWinnerDto): Promise<void> {
    if (!this.canOverrideWinners) {
      return;
    }
    if (!confirm(`Clear winner for ${winner.prizeName || 'this prize'} (rank ${winner.rank})?`)) {
      return;
    }
    this.busy = true;
    try {
      await firstValueFrom(
        this.api.overrideCareerChallengeWinner(this.id, winner.prizeTierId, {
          rank: winner.rank,
          clear: true
        })
      );
      this.snackBar.open('Winner cleared', 'Close', { duration: 2200 });
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  winnerLabel(winner: CareerChallengeWinnerDto): string {
    return winner.displayName || winner.teamName || winner.userId || 'Winner';
  }

  private parseCandidateKey(key: string): { teamId?: string | null; userId?: string | null } | null {
    if (!key) {
      return null;
    }
    if (key.startsWith('team:')) {
      return { teamId: key.slice(5), userId: null };
    }
    if (key.startsWith('user:')) {
      return { teamId: null, userId: key.slice(5) };
    }
    return null;
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title ?? error.error;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return 'Request failed';
  }

  prizeImageSrc(prize: CareerChallengePrizeTierDto): string {
    return resolveChallengeMediaSrc(prize.photoDisplayUrl, prize.photoUrl, 'prize');
  }
}